/**
 * Price drift (spec §9.1, plan §6 M2-07): a seeded sum of two slow sines per station and item, clamped to
 * ± `maxFraction`. Periods, phases and amplitudes come from `hash(station, item, salt)`, so every station and
 * every item wanders on its own and the same game time always gives the same price (saves hold no drift state).
 *
 * M4 replaces the whole static-with-drift model with stock-based prices (simulation spec §3.3); only `quote()`
 * calls this.
 */
import type { MarketConfig, StationId } from '../data/m2Schemas'
import { hashString, seededUnit } from '../space/hash'

export type DriftConfig = MarketConfig['drift']

/** Salt of the first wave; wave `w` uses `DRIFT_SALT + w × DRIFT_SALT_STRIDE + {0 period, 1 phase, 2 amplitude}`. */
const DRIFT_SALT = 0x0d1f7
const DRIFT_SALT_STRIDE = 4
/** Each wave's amplitude is this share range of `maxFraction`, so the sum can reach the clamp but rarely sits on it. */
const AMP_MIN_SHARE = 0.35
const AMP_SPAN_SHARE = 0.35
const TWO_PI = Math.PI * 2

/**
 * The drift fraction in [−maxFraction, +maxFraction] for `itemKey` (a ware id or `component:<type>`) at
 * `stationId` and game time `t` (seconds).
 */
export function driftFraction(stationId: StationId, itemKey: string, t: number, cfg: DriftConfig): number {
  const max = cfg.maxFraction
  if (max <= 0) return 0
  const s = hashString(stationId)
  const i = hashString(itemKey)
  const [lo, hi] = cfg.periodSeconds
  let sum = 0
  for (let w = 0; w < 2; w++) {
    const salt = DRIFT_SALT + w * DRIFT_SALT_STRIDE
    const period = lo + (hi - lo) * seededUnit(s, i, salt)
    const phase = TWO_PI * seededUnit(s, i, salt + 1)
    const amp = max * (AMP_MIN_SHARE + AMP_SPAN_SHARE * seededUnit(s, i, salt + 2))
    sum += amp * Math.sin((TWO_PI * t) / period + phase)
  }
  return sum > max ? max : sum < -max ? -max : sum
}

/** The drift price multiplier `1 + driftFraction(...)` (the `drift(t)` factor of the spec §9.1 sell price). */
export function drift(stationId: StationId, itemKey: string, t: number, cfg: DriftConfig): number {
  return 1 + driftFraction(stationId, itemKey, t, cfg)
}
